// ============================================================
// rtcManager.js — flux vidéo temps réel (WebRTC)
// Une session par joueur, partagée entre la grille (vignette légère)
// et le grand format (qualité maximale) : le panneau change de profil
// sur la même connexion au lieu de recréer un flux.
// ============================================================
export const RTC_SIGNAL_URL = "wss://monitor.rhode-rp.fr/ws";
const ICE_FALLBACK = [{ urls: "stun:stun.l.google.com:19302" }];
const CLOSE_DELAY_MS = 2500; // laisse le temps à grille <-> grand format d'échanger la session
const START_TIMEOUT_MS = 12000;
const MAX_RETRIES = 3;

const sessions = new Map(); // playerId -> session
let api = null;

export function setApi(fn) { api = fn; }

// Indices de débit pour le codec VP8 (x-google-*), pour éviter le démarrage à basse définition
export function tuneSdp(sdp, profile) {
  const [start, min] = profile === "grid" ? [250, 100] : [3000, 800];
  const hint = "x-google-start-bitrate=" + start + ";x-google-min-bitrate=" + min;
  const lines = sdp.split("\r\n");
  const vp8 = new Set();
  const hasFmtp = new Set();
  lines.forEach((l) => {
    const m = /^a=rtpmap:(\d+) VP8\/90000/.exec(l);
    if (m) vp8.add(m[1]);
  });
  lines.forEach((l) => {
    const f = /^a=fmtp:(\d+) /.exec(l);
    if (f && vp8.has(f[1])) hasFmtp.add(f[1]);
  });
  const out = [];
  lines.forEach((l) => {
    const f = /^a=fmtp:(\d+) (.*)$/.exec(l);
    if (f && vp8.has(f[1])) { out.push("a=fmtp:" + f[1] + " " + f[2] + ";" + hint); return; }
    out.push(l);
    const r = /^a=rtpmap:(\d+) VP8\/90000/.exec(l);
    if (r && !hasFmtp.has(r[1])) out.push("a=fmtp:" + r[1] + " " + hint);
  });
  return out.join("\r\n");
}

function desiredProfile(s) { return s.owners.has("focus") ? "focus" : "grid"; }

function emit(s) {
  s.snap = { state: s.state, stream: s.stream, stats: s.stats };
  s.subs.forEach((f) => { try { f(s.snap); } catch (e) { /* ignore */ } });
}

function makeSession(id) {
  return {
    id, owners: new Set(), state: "connecting", stream: null, stats: null,
    roomId: null, ws: null, pc: null, timers: {}, subs: new Set(), closed: false,
    attempts: 0, pending: [], prev: null, sentProfile: null, closeTimer: null,
    snap: { state: "connecting", stream: null, stats: null },
  };
}

function teardownConn(s) {
  clearTimeout(s.timers.start);
  clearInterval(s.timers.stats);
  const ws = s.ws, pc = s.pc, roomId = s.roomId;
  s.ws = null; s.pc = null; s.roomId = null; s.pending = []; s.prev = null;
  if (ws) {
    ws.onclose = null; ws.onmessage = null; ws.onerror = null;
    try { if (ws.readyState === 1) ws.send(JSON.stringify({ type: "hangup" })); } catch (e) { /* ignore */ }
    try { ws.close(); } catch (e) { /* ignore */ }
  }
  if (pc) { try { pc.close(); } catch (e) { /* ignore */ } }
  if (roomId && api) { try { api("/stream/stop", "POST", { roomId }); } catch (e) { /* ignore */ } }
}

function fail(s, why) {
  if (s.closed || s.state === "failed") return;
  console.warn("[RIRP-Stream] joueur " + s.id + " :", why);
  teardownConn(s);
  s.state = "failed"; s.stream = null; s.stats = null;
  emit(s);
  if (s.owners.size > 0 && s.attempts < MAX_RETRIES) {
    s.attempts++;
    clearTimeout(s.timers.retry);
    s.timers.retry = setTimeout(() => { s.timers.retry = null; connect(s); }, 3000 * s.attempts);
  }
}

function startStats(s) {
  clearInterval(s.timers.stats);
  s.prev = null;
  s.timers.stats = setInterval(async () => {
    const pc = s.pc;
    if (!pc) return;
    try {
      const r = await pc.getStats();
      let cur = null;
      r.forEach((x) => { if (x.type === "inbound-rtp" && x.kind === "video") cur = x; });
      if (cur) {
        const now = Date.now();
        let kbps = null;
        if (s.prev && now > s.prev.t) kbps = Math.round(((cur.bytesReceived - s.prev.b) * 8) / (now - s.prev.t));
        s.prev = { b: cur.bytesReceived, t: now };
        s.stats = { fps: Math.round(cur.framesPerSecond || 0), w: cur.frameWidth || 0, h: cur.frameHeight || 0, kbps };
        emit(s);
      }
    } catch (e) { /* ignore */ }
  }, 2000);
}

function syncProfile(s) {
  const want = desiredProfile(s);
  if (s.ws && s.ws.readyState === 1 && s.state === "live" && want !== s.sentProfile) {
    s.ws.send(JSON.stringify({ type: "profile", profile: want }));
    s.sentProfile = want;
  }
}

async function connect(s) {
  if (s.closed) return;
  teardownConn(s);
  s.state = "connecting"; s.stream = null; s.stats = null;
  emit(s);

  const profile = desiredProfile(s);
  let d = null;
  try { d = await api("/stream/start", "POST", { playerId: s.id, profile }); } catch (e) { d = null; }
  if (s.closed) { if (d && d.roomId) api("/stream/stop", "POST", { roomId: d.roomId }); return; }
  if (!d || !d.success) { fail(s, (d && d.error) || "démarrage refusé"); return; }

  s.roomId = d.roomId; s.sentProfile = profile; s.pending = [];
  const ws = new WebSocket(RTC_SIGNAL_URL);
  s.ws = ws;
  s.timers.start = setTimeout(() => { if (s.state !== "live") fail(s, "délai dépassé"); }, START_TIMEOUT_MS);
  ws.onopen = () => ws.send(JSON.stringify({ type: "join", callId: d.roomId, role: "staff", token: d.token }));
  ws.onclose = () => { if (ws === s.ws) fail(s, "signalisation fermée"); };
  ws.onerror = () => {};
  ws.onmessage = async (e) => {
    if (ws !== s.ws) return;
    let msg;
    try { msg = JSON.parse(e.data); } catch (err) { return; }

    if (msg.type === "joined") {
      const pc = new RTCPeerConnection({ iceServers: msg.iceServers || ICE_FALLBACK });
      s.pc = pc;
      pc.addTransceiver("video", { direction: "recvonly" });
      pc.onicecandidate = (ev) => {
        if (ev.candidate && ws.readyState === 1) ws.send(JSON.stringify({ type: "ice-candidate", candidate: ev.candidate }));
      };
      pc.onconnectionstatechange = () => { if (s.pc === pc && pc.connectionState === "failed") fail(s, "connexion WebRTC échouée"); };
      pc.ontrack = (ev) => {
        s.stream = (ev.streams && ev.streams[0]) || new MediaStream([ev.track]);
        s.state = "live"; s.attempts = 0;
        clearTimeout(s.timers.start);
        startStats(s);
        emit(s);
        syncProfile(s); // si le profil voulu a changé pendant la connexion
      };
      return;
    }
    const pc = s.pc;
    if (msg.type === "hangup" || msg.type === "error") { fail(s, "flux interrompu"); return; }
    if (!pc) return;

    if (msg.type === "peer-joined" && !pc.localDescription) {
      const offer = await pc.createOffer();
      const tuned = { type: offer.type, sdp: tuneSdp(offer.sdp, s.sentProfile || "focus") };
      await pc.setLocalDescription(tuned);
      ws.send(JSON.stringify({ type: "offer", sdp: tuned }));
    }
    if (msg.type === "answer") {
      await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      while (s.pending.length) { try { await pc.addIceCandidate(s.pending.shift()); } catch (err) { /* ignore */ } }
    }
    if (msg.type === "ice-candidate" && msg.candidate) {
      const c = new RTCIceCandidate(msg.candidate);
      if (pc.remoteDescription) { try { await pc.addIceCandidate(c); } catch (err) { /* ignore */ } }
      else s.pending.push(c);
    }
  };
}

function closeSession(s) {
  if (s.closed) return;
  s.closed = true;
  clearTimeout(s.closeTimer);
  clearTimeout(s.timers.retry);
  teardownConn(s);
  sessions.delete(s.id);
}

// --- API publique ---------------------------------------------------------
export function acquire(id, owner) {
  let s = sessions.get(id);
  if (!s) {
    s = makeSession(id);
    sessions.set(id, s);
    s.owners.add(owner);
    connect(s);
    return s;
  }
  clearTimeout(s.closeTimer); s.closeTimer = null;
  s.owners.add(owner);
  if (s.state === "failed" && !s.timers.retry) { s.attempts = 0; connect(s); }
  else syncProfile(s);
  return s;
}

export function release(id, owner) {
  const s = sessions.get(id);
  if (!s) return;
  s.owners.delete(owner);
  if (s.owners.size === 0) {
    clearTimeout(s.closeTimer);
    s.closeTimer = setTimeout(() => closeSession(s), CLOSE_DELAY_MS);
  } else {
    syncProfile(s);
  }
}

export function subscribe(id, fn) {
  const s = sessions.get(id);
  if (!s) return () => {};
  s.subs.add(fn);
  return () => { s.subs.delete(fn); };
}

export function closeAll() { Array.from(sessions.values()).forEach(closeSession); }

export function _debug() { return Array.from(sessions.values()).map((s) => ({ id: s.id, state: s.state, owners: Array.from(s.owners), sent: s.sentProfile })); }
